import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CourierUserApi, MailBankApi, ReferralApi } from '@shared/sdk-v1/apis';
import { apiConfig } from '../../shared/api-config';
import { getCourier, getCourierRealm, getCourierSetting } from '../../state/courierSlice';
import { login } from '../../state/authSlice';
import { AccessToken } from '@shared/sdk-v1/models';
import { showErrorToast, showSuccessToast } from '../../utils/toast';
import { useTrackEvent } from '../../utils/track';

interface UserData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  password: string;
  storeLocationId?: number;
  trnNumber?: string;
  businessName?: string;
  isCourier?: boolean;
  street1?: string;
  street2?: string;
  city?: string;
  state?: string;
  country?: string;
  referralSource?: string;
  wasReferred?: boolean;
  referrerQuery?: string;
}

export const useAccountCreation = () => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [isSendingVerification, setIsSendingVerification] = useState(false);
  const realm = useSelector(getCourierRealm);
  const courier = useSelector(getCourier);
  const courierSetting = useSelector(getCourierSetting);
  const requireVerification = courierSetting?.requireVerification;
  const track = useTrackEvent();

  /**
   * Best-effort: when the customer says they were referred, resolve the typed
   * referrer (referral code or name/email) to an existing account and create a
   * Referral record so reward attribution works. Optional — never blocks signup.
   */
  const linkReferrerIfProvided = async (userData: UserData, refereeId: number) => {
    if (!userData.wasReferred || !userData.referrerQuery?.trim() || !realm) return;
    try {
      const authApi = new CourierUserApi(apiConfig());
      const resolved = await authApi.courierUserResolveReferrer({
        query: userData.referrerQuery.trim(),
        realm,
      });
      if (resolved?.referrerId && courier?.id) {
        const referralApi = new ReferralApi(apiConfig());
        await referralApi.referralCreate({
          data: {
            referrerId: resolved.referrerId,
            refereeId,
            courierId: courier.id,
            signUpConfirmedOn: new Date(),
          },
        });
      }
    } catch (error) {
      // Optional field — log and continue without blocking the signup.
      console.error('Error linking referrer:', error);
    }
  };

  const createAccount = async (userData: UserData, turnstileToken?: string, turnstileSiteKey?: string) => {
    if (!realm) {
      showErrorToast({
        title: 'Error',
        message: 'Courier configuration not found',
      });
      return { success: false, userId: null };
    }

    try {
      setIsLoading(true);
      const authApi = new CourierUserApi(apiConfig());
      const response = await authApi.courierUserCreate({
        data: {
          email: userData.email,
          password: userData.password,
          realm,
          firstName: userData.firstName,
          lastName: userData.lastName,
          contactInformation: {
            mobilePhone: userData.phone,
          },
          storeLocationId: userData.storeLocationId,
          trnNumber: userData.trnNumber ? userData.trnNumber.replace(/\D/g, '') : undefined,
          businessName: userData.businessName || undefined,
          isCourier: userData.isCourier || undefined,
          addresses: userData.street1 ? [{
            name: 'Home',
            street1: userData.street1,
            street2: userData.street2 || '',
            city: userData.city || '',
            state: userData.state || '',
            country: userData.country || 'Jamaica',
            isPrimary: true,
          }] : undefined,
          referralSource: userData.referralSource || undefined,
          ...(turnstileToken ? { turnstileToken } : {}),
          ...(turnstileSiteKey ? { turnstileSiteKey } : {}),
        },
      });

      if (response.id) {
        const userId = response.id.toString();

        // Link a manually-entered referrer (optional; never blocks signup).
        await linkReferrerIfProvided(userData, response.id);

        if (requireVerification) {
          await sendEmailVerification(userId);
          setIsLoading(false);
          return { success: true, userId, needsVerification: true };
        } else {
          await loginUser(userData.email, userData.password);
          // No verification required: this is the furthest signup completion
          // this courier supports, so count it now.
          track('sign_up');
          return { success: true, userId, needsVerification: false };
        }
      } else {
        showErrorToast({
          title: 'Error',
          message:
            "Failed to create account, are you sure you don't have an account with this email?",
        });
        return { success: false, userId: null };
      }
    } catch (error) {
      console.log('error', error);
      showErrorToast({
        title: 'Error',
        message:
          "Failed to create account, are you sure you don't have an account with this email?",
      });
      return { success: false, userId: null };
    } finally {
      setIsLoading(false);
    }
  };

  const sendEmailVerification = async (userId: string) => {
    try {
      setIsSendingVerification(true);
      const mailApi = new MailBankApi(apiConfig());
      const response = await mailApi.sendOtp({
        userId: userId,
        method: 'email',
      });

      if (response) {
        showSuccessToast({
          title: 'Success',
          message: 'Email verification sent',
        });
      }
    } catch (error) {
      showErrorToast({
        title: 'Error',
        message: 'Failed to send verification email',
      });
    } finally {
      setIsSendingVerification(false);
    }
  };

  const loginUser = async (email: string, password: string) => {
    if (!realm) {
      console.error('No realm available for login');
      return;
    }

    try {
      const authApi = new CourierUserApi(apiConfig());
      const accessToken: AccessToken = (await authApi.courierUserLogin({
        credentials: {
          email: email,
          password: password,
          realm: realm,
        },
        include: 'user',
      })) as AccessToken;

      if (accessToken.id) {
        console.log('Login successful, dispatching login action', accessToken);
        dispatch(login(accessToken));
        showSuccessToast({
          title: 'Success',
          message: 'Account created successfully',
        });
      } else {
        console.error('Login failed: No access token received');
        showErrorToast({
          title: 'Error',
          message: 'Failed to login after account creation',
        });
      }
    } catch (error) {
      console.error('Login error:', error);
      showErrorToast({
        title: 'Error',
        message: 'Failed to login after account creation',
      });
    }
  };

  return {
    isLoading,
    isSendingVerification,
    createAccount,
    sendEmailVerification,
    loginUser,
    requireVerification,
  };
};
